import { PaginatedResponse } from '@/core/models/PaginatedResponse';
import { StoreState } from '@/core/models/store.interface';
import { LocationQuery } from 'vue-router';
import { Module } from 'vuex';
import { CommercialAgreementState } from './commercial-agreements/commercial-agreement.store';
import { ContactState } from './contacts/contact.store';
import contractTemplate, { ContractTemplateState } from './contract-templates/contract-template.store';
import { Customer, CustomerDTO } from './customer.interface';
import { CustomerService } from './customer.service';
import document, { DocumentState } from './documents/document.store';
import employee, { EmployeeState } from './employees/employee.store';
import { IndexCommercialAgreementBatchState } from './index-commercial-agreement-batches/index-commercial-agreement-batch.store';
import { PerformanceCodeTriggerExceptionState } from './performance-code-trigger-exceptions/performance-code-trigger-exception.store';
import { PerformanceCodeTriggerState } from './performance-code-triggers/performance-code-trigger.store';
import performanceOverview, { PerformanceOverviewState } from './performances-overview/performance-overview.store';
import { WorkCycleState } from './work-cycles/work-cycle.store';

const service = new CustomerService();

export interface CustomerState {
   customersOverview?: PaginatedResponse<Customer>;

   //sub-modules
   contact?: ContactState;
   contractTemplate?: ContractTemplateState;
   workCycle?: WorkCycleState;
   commercialAgreement?: CommercialAgreementState;
   indexCommercialAgreementBatch?: IndexCommercialAgreementBatchState;
   performanceCodeTrigger?: PerformanceCodeTriggerState;
   performanceCodeTriggerException?: PerformanceCodeTriggerExceptionState;
   employee?: EmployeeState;
   performanceOverview?: PerformanceOverviewState;
   document?: DocumentState;
}

const CustomerStore: Module<CustomerState, StoreState> = {
   namespaced: true,
   state: {},
   getters: {},
   mutations: {
      SET_CUSTOMERS_OVERVIEW(state, overview?: PaginatedResponse<Customer>) {
         state.customersOverview = overview;
      },
      ADD_CUSTOMER(state, customer: Customer) {
         state.customersOverview?.data.push(customer);
      },
      CHANGE_CUSTOMER(state, customer: Customer) {
         const index = (state.customersOverview?.data || []).findIndex((c) => c.id === customer.id);
         if (index > -1) state.customersOverview?.data.splice(index, 1, customer);
      },
      DELETE_CUSTOMER(state, customerId: number) {
         const index = (state.customersOverview?.data || []).findIndex((c) => c.id === customerId);
         if (index > -1) state.customersOverview?.data.splice(index, 1);
      },
   },
   actions: {
      async searchCustomers(context, query?: LocationQuery) {
         return await service.getCustomers(query);
      },
      async getCustomers(context, query?: LocationQuery) {
         const response = await service.getCustomers(query);
         context.commit('SET_CUSTOMERS_OVERVIEW', response);
         return response;
      },
      async getCustomer(context, payload: { customerId: number; query?: LocationQuery; cancelable?: boolean }) {
         const response = await service.getCustomer(payload.customerId, payload.query, { cancelable: payload.cancelable });
         return response;
      },
      async createCustomer(context, customer: CustomerDTO) {
         const response = await service.postCustomer(customer);
         context.commit('ADD_CUSTOMER', customer);
         return response;
      },
      async changeCustomer(context, payload: { customerId: number; customer: CustomerDTO }) {
         const response = await service.putCustomer(payload.customerId, payload.customer);
         context.commit('CHANGE_CUSTOMER', response);
         return response;
      },
      async deleteCustomer(context, customerId: number) {
         const response = await service.deleteCustomer(customerId);
         context.commit('DELETE_CUSTOMER', customerId);
         return response;
      },
   },
   modules: {
      employee,
      contractTemplate,
      performanceOverview,
      document,
   },
};

export default CustomerStore;
